import ROUTES, { ACCOUNTS_CARDS_ROUTES } from 'app/constants/routes';
import React, { createContext, useContext, useReducer } from 'react';
import NonWorkingHours from '../pages/digital-account/ui/components/dialogs/off-hours';
import { RegisteredUserDetails } from '../shared/types/digital-account/register';
import { AccountsDetails, CompanyDetails } from '../shared/types/digital-account/steps';
import { useCustomNavigate } from '../utils/customHooks/useCustomNavigate';

export interface DigitalContextValue {
  state: InitialState;
  dispatch: React.Dispatch<DigitalAction>;
}

const DigitalContext = createContext<DigitalContextValue | undefined>(undefined);

interface DigitalProviderProps {
  children: React.ReactNode;
}

const initialState: InitialState = {
  business_key: localStorage.getItem('business_key') || '',
  step: 'register',
  nonWorkingHours: false,
  user: null,
  accounts: null,
  company: null,
};

interface InitialState {
  business_key: string | null;
  step: 'register' | 'consent' | 'company-info' | 'currency';
  user: RegisteredUserDetails | null;
  accounts: AccountsDetails | null;
  company: CompanyDetails | null;
  nonWorkingHours: boolean;
}

// Action Types
export type DigitalAction =
  | { type: 'REGISTER'; payload: { business_key: InitialState['business_key'] } }
  | { type: 'GETTING_USER_INFO'; payload: InitialState['user'] }
  | { type: 'ACCOUNTS_DETAILS'; payload: InitialState['accounts'] }
  | { type: 'COMPANY_INFO'; payload: InitialState['company'] }
  | { type: 'NON_WORKING_HOUR'; payload: InitialState['nonWorkingHours'] };

export const DigitalProvider: React.FC<DigitalProviderProps> = ({ children }) => {
  const [isWorkingHours, setIsWorkingHours] = React.useState(false);
  const [state, dispatch] = useReducer(reducer, initialState);
  const navigate = useCustomNavigate();

  function reducer(newState: InitialState, action: DigitalAction): InitialState {
    if (typeof action.payload === 'object' && action.payload?.business_key) {
      newState['business_key'] = action.payload['business_key'];
      localStorage.setItem('business_key', action.payload['business_key']);
    }
    switch (action.type) {
      case 'GETTING_USER_INFO':
        newState['user'] = action.payload;
        break;
      case 'ACCOUNTS_DETAILS':
        newState['accounts'] = action.payload;
        break;
      case 'COMPANY_INFO':
        newState['company'] = action.payload;
        break;
      case 'NON_WORKING_HOUR':
        setIsWorkingHours(true);
        break;
      default:
        return newState;
    }

    return newState;
  }

  const navigateToMainPage = () => {
    navigate(`${ROUTES.accountsCards.replace('/:lang/', '')}/${ACCOUNTS_CARDS_ROUTES.accounts}`);
  };

  return (
    <DigitalContext.Provider value={{ state, dispatch }}>
      {children}
      <NonWorkingHours open={isWorkingHours} navigate={navigateToMainPage} />
    </DigitalContext.Provider>
  );
};

export function useDigitalContext(): DigitalContextValue {
  const context = useContext(DigitalContext);
  if (context === undefined) {
    throw new Error('useDigitalContext must be used within a DigitalProvider');
  }

  return context;
}

export default DigitalContext;
